import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { motion } from "framer-motion";
import { Check, Sparkles, Crown, Zap, ArrowLeft, ArrowRight } from "lucide-react";
import api from "@/lib/api";
import { toast } from "sonner";
import { useAuth } from "@/lib/auth";
import FreeModeBanner from "@/components/FreeModeBanner";

const PLAN_ICON = { free: Sparkles, plus: Zap, pro: Crown };

const RZP_SCRIPT = "https://checkout.razorpay.com/v1/checkout.js";

function loadRazorpay() {
  return new Promise((resolve) => {
    if (window.Razorpay) return resolve(true);
    const s = document.createElement("script");
    s.src = RZP_SCRIPT;
    s.onload = () => resolve(true);
    s.onerror = () => resolve(false);
    document.body.appendChild(s);
  });
}

export default function Billing() {
  const nav = useNavigate();
  const { user, refresh } = useAuth();
  const [plans, setPlans] = useState([]);
  const [me, setMe] = useState(null);
  const [busy, setBusy] = useState(false);
  const [freeMode, setFreeMode] = useState(false);
  const [geo, setGeo] = useState({ currency: "INR", symbol: "₹" });

  useEffect(() => {
    api.get("/billing/plans").then((r) => setPlans(r.data.plans));
    api.get("/billing/me").then((r) => setMe(r.data));
    api.get("/billing/geo").then((r) => setGeo(r.data)).catch(() => {});
    api.get("/config/public").then((r) => setFreeMode(!!r.data?.free_mode)).catch(() => {});
    loadRazorpay();
  }, []);

  const isINR = geo.currency === "INR";
  const planPrice = (p) => (isINR ? `₹${p.price_inr ?? 0}` : `$${p.price_usd ?? 0}`);

  const startCheckout = async (planId) => {
    setBusy(true);
    try {
      const ok = await loadRazorpay();
      if (!ok) {
        toast.error("Couldn't load payment gateway. Check your connection.");
        setBusy(false);
        return;
      }
      const { data } = await api.post("/billing/checkout", { plan: planId });

      const options = {
        key: data.key_id,
        amount: data.amount,
        currency: data.currency,
        name: "Studivault",
        description: `${data.plan_name} — 30 days`,
        order_id: data.order_id,
        prefill: data.prefill,
        theme: { color: "#7C5CFF" },
        handler: async (resp) => {
          try {
            const { data: v } = await api.post("/billing/verify", {
              razorpay_order_id: resp.razorpay_order_id,
              razorpay_payment_id: resp.razorpay_payment_id,
              razorpay_signature: resp.razorpay_signature,
            });
            if (v.applied) {
              toast.success(`You're on ${v.plan.toUpperCase()} — welcome aboard`);
              await refresh();
              const r = await api.get("/billing/me");
              setMe(r.data);
            } else {
              toast.error("Payment could not be confirmed.");
            }
          } catch (e) {
            toast.error(e?.response?.data?.detail || "Payment verification failed");
          } finally {
            setBusy(false);
          }
        },
        modal: {
          ondismiss: () => {
            toast.info("Checkout cancelled");
            setBusy(false);
          },
        },
      };

      const rzp = new window.Razorpay(options);
      rzp.on("payment.failed", (resp) => {
        toast.error(resp?.error?.description || "Payment failed");
        setBusy(false);
      });
      rzp.open();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Couldn't start checkout");
      setBusy(false);
    }
  };

  const currentPlan = me?.plan?.id || user?.plan || "free";

  return (
    <div className="max-w-[1180px] mx-auto px-5 md:px-8 py-10 md:py-14">
      <button
        onClick={() => nav(-1)}
        className="text-[13px] font-medium text-[#B0AEBC] hover:text-white flex items-center gap-1 mb-8"
        data-testid="billing-back"
      >
        <ArrowLeft size={14} /> Back
      </button>

      <motion.div initial={{ opacity: 0, y: 8 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.5 }}>
        <div className="eyebrow">Plans</div>
        <h1 className="display-2 mt-2">Pick a <span className="grad-text">tier</span></h1>
        <p className="text-[15px] text-[#B0AEBC] mt-3 max-w-lg">
          Free forever for casual study. Upgrade when you're ready to move faster.
        </p>
      </motion.div>

      <FreeModeBanner className="mt-8" />

      {me?.active && (
        <motion.div
          initial={{ opacity: 0, y: 6 }}
          animate={{ opacity: 1, y: 0 }}
          className="mt-8 surface px-5 py-3.5 inline-flex items-center gap-3"
          data-testid="billing-current-status"
        >
          <span className="text-[12px] uppercase tracking-wider text-[#8E8B99]">Current</span>
          <span className="text-[15px] font-semibold text-white">{me.plan.name}</span>
          <span className="text-[12px] text-[#B0AEBC]">until {new Date(me.plan_until).toLocaleDateString()}</span>
        </motion.div>
      )}

      <div className="grid md:grid-cols-3 gap-4 mt-10">
        {plans.map((p, i) => {
          const Icon = PLAN_ICON[p.id] || Sparkles;
          const isCurrent = p.id === currentPlan && (p.id === "free" || me?.active);
          const isPro = p.id === "pro";
          return (
            <motion.div
              key={p.id}
              initial={{ opacity: 0, y: 16 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: i * 0.08, duration: 0.55 }}
              className={`surface p-7 relative overflow-hidden ${isPro ? "glow-card" : ""}`}
              data-testid={`plan-${p.id}`}
              style={isPro ? { borderColor: "rgba(124,92,255,0.40)" } : {}}
            >
              {isPro && (
                <div
                  className="absolute -top-12 -right-12 w-40 h-40 rounded-full pointer-events-none"
                  style={{ background: "radial-gradient(circle, rgba(124,92,255,0.40), transparent 65%)", filter: "blur(20px)" }}
                />
              )}
              {p.id === "plus" && (
                <span className="absolute top-4 right-4 chip chip-purple text-[10px]">Popular</span>
              )}
              <div className="relative">
                <div className="w-11 h-11 rounded-2xl bg-gradient-to-br from-[#7C5CFF]/25 to-[#5AC8FA]/15 border border-white/10 flex items-center justify-center mb-5" style={{ boxShadow: "0 0 20px -4px rgba(124,92,255,0.55)" }}>
                  <Icon size={20} className="text-[#B8A4FF]" strokeWidth={1.8} />
                </div>
                <h3 className="text-[20px] font-semibold text-white">{p.name}</h3>
                <p className="text-[13px] text-[#B0AEBC] mt-1">{p.tagline}</p>
                <div className="mt-5 flex items-baseline gap-1">
                  <span className="text-[40px] font-bold tracking-tight text-white tabular-nums">
                    {planPrice(p)}
                  </span>
                  <span className="text-[13px] text-[#8E8B99]">{p.id === "free" ? "" : "/ 30 days"}</span>
                </div>
                {p.id !== "free" && (
                  <p className="text-[11px] text-[#8E8B99] mt-1" data-testid={`plan-billing-note-${p.id}`}>
                    One-time payment · does not auto-renew
                  </p>
                )}

                <ul className="mt-6 space-y-2.5">
                  {p.features.map((f, idx) => (
                    <li key={idx} className="flex items-start gap-2 text-[13.5px] text-[#E4E4E7]">
                      <Check size={14} className="text-[#5AC8FA] mt-1 shrink-0" strokeWidth={2.5} />
                      <span>{f}</span>
                    </li>
                  ))}
                </ul>

                <button
                  onClick={() => !isCurrent && !(freeMode && p.id !== "free") && p.id !== "free" && startCheckout(p.id)}
                  disabled={isCurrent || p.id === "free" || (freeMode && p.id !== "free") || busy}
                  data-testid={`plan-cta-${p.id}`}
                  className={`btn ${isPro ? "btn-primary" : "btn-secondary"} w-full mt-7 py-3 text-[14px] ${isCurrent || p.id === "free" || (freeMode && p.id !== "free") ? "opacity-60 cursor-not-allowed" : ""}`}
                >
                  {isCurrent
                    ? "Current plan"
                    : p.id === "free"
                    ? "Free forever"
                    : freeMode
                    ? <>✨ Unlocked for limited time</>
                    : <>Upgrade to {p.name} <ArrowRight size={13} /></>}
                </button>
              </div>
            </motion.div>
          );
        })}
      </div>

      <p className="text-[12px] text-[#8E8B99] mt-8 text-center" data-testid="billing-disclosure">
        Secured by Razorpay · One-time payment for 30 days of access · <span className="text-[#B0AEBC]">does not auto-renew</span> — you'll never be charged again unless you choose to · prices in {geo.currency}
      </p>
    </div>
  );
}
